'use client';

import { useState, useMemo } from 'react';
import { Copy, CopyCheck, RefreshCw } from 'lucide-react';
import Feedback from 'src/features/plugins/outlook/components/Feedback';
import { Separator } from 'src/shared/components/ui/separator';
import { Button } from 'src/shared/components/ui/buttons/button';

function gatherDiagnostics(): string {
  const lines: string[] = [];
  const now = new Date();

  lines.push(`Timestamp: ${now.toISOString()}`);
  lines.push(`User Agent: ${navigator.userAgent}`);
  lines.push(`URL: ${window.location.href}`);
  lines.push(`Platform: ${navigator.platform ?? 'unknown'}`);

  const hasOffice = typeof window.Office !== 'undefined' && !!window.Office;
  lines.push(`Office JS loaded: ${hasOffice ? 'yes' : 'no'}`);

  if (hasOffice) {
    try {
      lines.push(`Office context: ${Office.context ? 'yes' : 'no'}`);
      lines.push(`Office mailbox: ${Office.context?.mailbox ? 'yes' : 'no'}`);
      lines.push(`Office item: ${Office.context?.mailbox?.item ? 'yes' : 'no'}`);

      const diag = (Office.context as any)?.diagnostics;
      if (diag) {
        lines.push(`Host: ${diag.hostName ?? 'unknown'}`);
        lines.push(`Host version: ${diag.hostVersion ?? 'unknown'}`);
        lines.push(`Host platform: ${diag.platform ?? 'unknown'}`);
      }

      lines.push(`Office.onReady: ${typeof Office.onReady === 'function' ? 'yes' : 'no'}`);
      lines.push(`Office.EventType: ${Office.EventType ? 'yes' : 'no'}`);
    } catch (e) {
      lines.push(`Office probe error: ${String(e)}`);
    }
  }

  try {
    const hostInfo = sessionStorage.getItem('hostInfoValue');
    lines.push(`hostInfoValue: ${hostInfo ?? 'null'}`);
    lines.push(`sessionStorage keys: ${Object.keys(sessionStorage).join(', ') || 'none'}`);
  } catch {
    lines.push(`sessionStorage: inaccessible`);
  }

  try {
    const hasOSF = typeof (window as any).OSF !== 'undefined';
    lines.push(`OSF loaded: ${hasOSF ? 'yes' : 'no'}`);
    if (hasOSF) {
      lines.push(`OSF._OfficeAppFactory: ${(window as any).OSF?._OfficeAppFactory ? 'yes' : 'no'}`);
    }
  } catch {
    lines.push(`OSF probe: inaccessible`);
  }

  lines.push(`Timeout: 20s`);

  return lines.join('\n');
}

function copyToClipboard(text: string): void {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

interface OfficeConnectionErrorProps {
  onRetry: () => void;
}

export default function OfficeConnectionError({ onRetry }: OfficeConnectionErrorProps) {
  const [copied, setCopied] = useState(false);
  const diagnostics = useMemo(() => gatherDiagnostics(), []);

  const handleCopy = () => {
    copyToClipboard(diagnostics);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <Feedback testId="outlook-office-error">
      <div className="flex flex-col justify-center w-full gap-3">
        <Feedback.Title text="Outlook isn't connecting" className="text-center" />

        <Separator className="mb-1" />

        <Feedback.Description text="The Tekst add-in couldn't connect to Outlook. This usually happens when Outlook is still loading or the connection timed out." />

        <span className="text-typography-grey text-sm">
          Try reloading, or if the issue persists, copy the diagnostics below and include them in a support ticket.
        </span>

        <Button variant="outline" className="w-full gap-2 mt-1" onClick={onRetry}>
          <RefreshCw size={14} />
          Reload add-in
        </Button>

        <div className="rounded-sm border border-base2-200 bg-base2-50 px-3 py-3 mt-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-base2-700">Diagnostics</span>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-base2-500 hover:text-base2-700 transition-colors"
            >
              {copied ? (
                <>
                  <CopyCheck size={12} className="text-success" />
                  <span className="text-success">Copied</span>
                </>
              ) : (
                <>
                  <Copy size={12} />
                  <span>Copy diagnostics</span>
                </>
              )}
            </button>
          </div>
          <pre className="text-[10px] leading-relaxed text-base2-600 whitespace-pre-wrap break-all max-h-[120px] overflow-y-auto">
            {diagnostics}
          </pre>
        </div>
      </div>
    </Feedback>
  );
}
